// Updated BidManagement.tsx with improved styling
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Tooltip,
  useTheme,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';

import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';

import FilterAltIcon from '@mui/icons-material/FilterAlt';
import { httpRequest } from 'src/services/httpRequest';
import toast from 'react-hot-toast';
import EditBid from './EditBid';
import {
  Bid,
  BidFormData,
  CategoryItem,
  StatusItem,
  TypeItem,
  ApiResponse
} from './types';
import { ClearAll, Delete, Warning } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { RootState } from 'src/redux-store/appStore';

// Dialog state interface
interface DeleteDialogState {
  open: boolean;
  bidId: string;
  bidTitle: string;
}

const BidManagement: React.FC = () => {
  const theme = useTheme();

  // States
  const [searchFormData, setSearchFormData] = useState<BidFormData>({
    title: '',
    contractNo: '',
    type: [],
    categories: [],
    bidDeadline: null,
    status: []
  });

  const [selectedBid, setSelectedBid] = useState<Bid | null>(null);
  const [showSearch, setShowSearch] = useState(true);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [bids, setBids] = useState<Bid[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);

  const userDetails = useSelector((state: RootState) => state.user.userDetails);
  const userRole = userDetails.UserRole || '';

  // API Data States
  const [categories, setCategories] = useState<CategoryItem[]>([]);
  const [statuses, setStatuses] = useState<StatusItem[]>([]);
  const [bidTypes, setBidTypes] = useState<TypeItem[]>([]);

  // Category and Status mapping for display
  const [categoryMap, setCategoryMap] = useState<{ [key: number]: string }>({});
  const [statusMap, setStatusMap] = useState<{ [key: number]: string }>({});
  const [typeMap, setTypeMap] = useState<{ [key: number]: string }>({});

  // Dialog state
  const [deleteDialog, setDeleteDialog] = useState<DeleteDialogState>({
    open: false,
    bidId: '',
    bidTitle: ''
  });

  const [openCategorySelect, setOpenCategorySelect] = useState(false);
  const [openStatusSelect, setOpenStatusSelect] = useState(false);
  const [openTypeSelect, setOpenTypeSelect] = useState(false);

  // API functions
  const fetchBids = async (
    pageNum: number,
    pageSize: number,
    criteria: BidFormData = searchFormData
  ) => {
    setLoading(true);
    try {
      const activeCriteria = criteria || searchFormData;
      // Construct query parameters using the passed criteria instead of state
      const queryParams = new URLSearchParams({
        Page: (pageNum + 1).toString(),
        Size: pageSize.toString(),
        ...(activeCriteria?.title && { Title: activeCriteria?.title }),
        ...(activeCriteria?.categories.length > 0 && {
          Categories: activeCriteria?.categories.join(',')
        }),
        ...(activeCriteria?.status.length > 0 && {
          Status: activeCriteria?.status.join(',')
        }),
        ...(activeCriteria?.type.length > 0 && {
          Type: activeCriteria?.type.join(',')
        }),
        ...(activeCriteria?.bidDeadline && {
          BidDeadLine: activeCriteria?.bidDeadline.toISOString()
        })
      });

      const response = (await httpRequest(
        'GET',
        `/bid_portal/bid_details/get?${queryParams.toString()}`,
        undefined
      )) as ApiResponse<Bid[]>;

      if (response.status === 'success' && response.data.Data) {
        // Transform API data to component format
        const transformedBids = response?.data?.Data?.map((bid) => ({
          bidId: bid?.BidID?.toString() || '',
          title: bid?.Title || '',
          adminNote: bid?.AdminNote || '',
          description: bid?.Description || '',
          category: bid?.Categories?.map((cat) => cat.CategoryName) || [],
          status: bid?.Status || '',
          goLiveDate: bid?.GoLiveDate,
          bidDeadline: bid?.BidDeadLineDate,
          awardDate: bid?.AwardDate,
          departmentID: bid?.DepartmentID,
          statusID: bid?.StatusID,
          categoryIDs: bid?.CategoryIDs,
          // Keep the original data for reference
          BidID: bid?.BidID,
          TypeID: bid?.TypeID,
          ContractNo: bid?.ContractNo,
          Type: bid?.Type,
          effectiveDate: bid?.EffectiveDate
        }));

        setBids(transformedBids);
        setTotalRecords(response.data.TotalRecords || 0);
      }
    } catch (error) {
      console.error('Error fetching bids:', error);
      toast.error('An error occurred while fetching bids');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = (await httpRequest(
        'GET',
        `/bid_portal/category/get_all`,
        undefined
      )) as ApiResponse<CategoryItem[]>;

      if (response.status === 'success' && response.data.data) {
        setCategories(response.data.data);

        // Create a mapping of category IDs to names
        const mapping: { [key: number]: string } = {};
        response.data.data.forEach((cat) => {
          // Only include active categories
          if (cat.Active) {
            mapping[cat.CategoryID] = cat.CategoryName;
          }
        });
        setCategoryMap(mapping);
      } else {
        toast.error('Failed to fetch categories');
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchStatuses = async () => {
    try {
      const response = (await httpRequest(
        'GET',
        `/bid_portal/status/get_all`,
        undefined
      )) as ApiResponse<StatusItem[]>;

      if (response.status === 'success' && response.data.data) {
        setStatuses(response.data.data);

        // Create a mapping of status IDs to names
        const mapping: { [key: number]: string } = {};
        response.data.data.forEach((status) => {
          // Only include active statuses
          if (status.Active) {
            mapping[status.StatusID] = status.Status;
          }
        });
        setStatusMap(mapping);
      } else {
        toast.error('Failed to fetch statuses');
      }
    } catch (error) {
      console.error('Error fetching statuses:', error);
    }
  };

  const fetchTypes = async () => {
    try {
      const response = (await httpRequest(
        'GET',
        `/bid_portal/type/get_all`,
        undefined
      )) as ApiResponse<TypeItem[]>;

      if (response.status === 'success' && response.data.data) {
        setBidTypes(response.data.data);

        // Create a mapping of type IDs to names
        const mapping: { [key: number]: string } = {};
        response.data.data.forEach((type) => {
          // Only include active types
          if (type.Active) {
            mapping[type.TypeID] = type.Type;
          }
        });
        setTypeMap(mapping);
      } else {
        toast.error('Failed to fetch bid types');
      }
    } catch (error) {
      console.error('Error fetching bid types:', error);
    }
  };

  // Load reference data on component mount
  useEffect(() => {
    fetchCategories();
    fetchStatuses();
    fetchTypes();
  }, []);

  useEffect(() => {
    fetchBids(page, rowsPerPage);
  }, [page, rowsPerPage]);

  const handleSearch = () => {
    setPage(0);
    fetchBids(0, rowsPerPage);
  };

  const handleClear = () => {
    const clearedCriteria = {
      title: '',
      contractNo: '',
      type: [],
      categories: [],
      bidDeadline: null,
      status: []
    };

    // Update the state
    setSearchFormData(clearedCriteria);
    setPage(0);

    // Call fetchBids with the cleared criteria directly
    fetchBids(0, rowsPerPage, clearedCriteria);
  };

  const handleEdit = (bid: Bid) => {
    // Format the bid data for editing
    const formattedBid = {
      ...bid,
      bidId: bid.BidID?.toString() || bid.bidId,
      title: bid.Title || bid.title,
      adminNote: bid.AdminNote || bid.adminNote,
      description: bid.Description || bid.description,
      // Ensure both original and mapped data is preserved
      ContractNo: bid.ContractNo || '',
      Type: bid.Type || '',
      effectiveDate: bid.effectiveDate
    };

    setSelectedBid(formattedBid);
    setShowSearch(false);
  };

  const handleEditSuccess = () => {
    setShowSearch(true);
    setSelectedBid(null);
    fetchBids(0, rowsPerPage);
  };

  const handleEditCancel = () => {
    setShowSearch(true);
    setSelectedBid(null);
  };

  // Pagination handlers
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Get status color based on status name
  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'open':
      case 'active':
        return theme.palette.success.main;
      case 'closed':
      case 'inactive':
        return theme.palette.error.main;
      case 'pending':
      case 'in review':
        return theme.palette.warning.main;
      case 'awarded':
        return theme.palette.info.main;
      default:
        return theme.palette.grey[500];
    }
  };

  // Open the delete confirmation dialog
  const openDeleteDialog = (bidId: string, bidTitle: string) => {
    setDeleteDialog({
      open: true,
      bidId,
      bidTitle
    });
  };

  // Close the delete confirmation dialog
  const closeDeleteDialog = () => {
    setDeleteDialog({
      open: false,
      bidId: '',
      bidTitle: ''
    });
  };

  // Handle the actual delete operation
  const handleDeleteConfirm = async () => {
    try {
      // Updated endpoint for delete operation
      const response = await httpRequest(
        'DELETE',
        `/bid_portal/bid_details/delete/${deleteDialog.bidId}`
      );

      if (response.status === 'success') {
        toast.success('Bid deactivated successfully');
        fetchBids(page, rowsPerPage); // Refresh the list
      } else {
        toast.error(response.message || 'Failed to deactivate bid');
      }
    } catch (error) {
      console.error('Error deleting bid:', error);
      toast.error('Failed to deactivate bid. Please try again later.');
    } finally {
      closeDeleteDialog();
    }
  };

  // Results Table Component
  const ResultsTable = () => (
    <Card
      elevation={2}
      sx={{
        borderRadius: 2,
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
        border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
        overflow: 'hidden' // Important to prevent content from extending outside the card
      }}
    >
      <CardHeader
        title={
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <Typography variant="h6">Bid Results</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
              {totalRecords} {totalRecords === 1 ? 'bid' : 'bids'} found
            </Typography>
          </Box>
        }
        sx={{
          bgcolor: `linear-gradient(to right, ${alpha(
            theme.palette.primary.main,
            0.05
          )}, ${alpha(theme.palette.primary.main, 0.02)})`,
          borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
        }}
      />
      <TableContainer>
        <Table
          sx={{
            '& .MuiTableCell-head': {
              bgcolor: alpha(theme.palette.primary.main, 0.03),
              fontWeight: 'bold',
              color: theme.palette.text.primary,
              py: 2
            },
            '& .MuiTableCell-body': {
              py: 1.8
            }
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  fontWeight: 'bold',
                  color: theme.palette.text.primary,
                  py: 1.5,
                  position: 'sticky',
                  top: 0,
                  zIndex: 1100,
                  backgroundColor: theme.palette.background.paper,
                  boxShadow: `0 1px 0 ${alpha(theme.palette.divider, 0.2)}`,
                  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.3)}`
                }}
              >
                Contract No.
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 'bold',
                  color: theme.palette.text.primary,
                  py: 1.5,
                  position: 'sticky',
                  top: 0,
                  zIndex: 1100,
                  backgroundColor: theme.palette.background.paper,
                  boxShadow: `0 1px 0 ${alpha(theme.palette.divider, 0.2)}`,
                  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.3)}`
                }}
              >
                Title
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 'bold',
                  color: theme.palette.text.primary,
                  py: 1.5,
                  position: 'sticky',
                  top: 0,
                  zIndex: 1100,
                  backgroundColor: theme.palette.background.paper,
                  boxShadow: `0 1px 0 ${alpha(theme.palette.divider, 0.2)}`,
                  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.3)}`
                }}
              >
                Admin Notes
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 'bold',
                  color: theme.palette.text.primary,
                  py: 1.5,
                  position: 'sticky',
                  top: 0,
                  zIndex: 1100,
                  backgroundColor: theme.palette.background.paper,
                  boxShadow: `0 1px 0 ${alpha(theme.palette.divider, 0.2)}`,
                  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.3)}`
                }}
              >
                Description
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 'bold',
                  color: theme.palette.text.primary,
                  py: 1.5,
                  position: 'sticky',
                  top: 0,
                  zIndex: 1100,
                  backgroundColor: theme.palette.background.paper,
                  boxShadow: `0 1px 0 ${alpha(theme.palette.divider, 0.2)}`,
                  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.3)}`
                }}
              >
                Category
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 'bold',
                  color: theme.palette.text.primary,
                  py: 1.5,
                  position: 'sticky',
                  top: 0,
                  zIndex: 1100,
                  backgroundColor: theme.palette.background.paper,
                  boxShadow: `0 1px 0 ${alpha(theme.palette.divider, 0.2)}`,
                  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.3)}`
                }}
              >
                Status
              </TableCell>

              {userRole === 'Admin' && (
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: 'bold',
                    color: theme.palette.text.primary,
                    py: 1.5,
                    position: 'sticky',
                    top: 0,
                    zIndex: 1100,
                    backgroundColor: theme.palette.background.paper,
                    boxShadow: `0 1px 0 ${alpha(theme.palette.divider, 0.2)}`,
                    borderBottom: `1px solid ${alpha(
                      theme.palette.divider,
                      0.3
                    )}`
                  }}
                >
                  Actions
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {bids.length > 0 ? (
              bids.map((bid) => (
                <TableRow
                  key={bid.bidId}
                  hover
                  sx={{
                    '&:nth-of-type(even)': {
                      bgcolor: alpha(theme.palette.background.default, 0.4)
                    },
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.light, 0.05)
                    },
                    transition: 'background-color 0.2s ease',
                    // Lighter borders instead of dark lines
                    '& .MuiTableCell-root': {
                      borderBottom: `1px solid ${alpha(
                        theme.palette.divider,
                        0.2
                      )}`,
                      py: 1.75
                    }
                  }}
                >
                  <TableCell sx={{ fontWeight: 'medium', py: 1.75 }}>
                    {bid.ContractNo}
                  </TableCell>
                  <TableCell sx={{ py: 1.75 }}>{bid.title}</TableCell>
                  <TableCell sx={{ py: 1.75 }}>
                    <Tooltip title={bid.adminNote} arrow>
                      <Typography
                        sx={{
                          maxWidth: 150,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        {bid.adminNote || '—'}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ py: 1.75 }}>
                    <Tooltip title={bid.description} arrow>
                      <Typography
                        sx={{
                          maxWidth: 150,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        {bid.description || '—'}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ py: 1.75 }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {bid.category.map((cat) => (
                        <Chip
                          key={cat}
                          label={cat}
                          size="small"
                          sx={{
                            height: 24,
                            fontSize: '0.75rem',
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            color: theme.palette.primary.main,
                            borderRadius: 1
                          }}
                        />
                      ))}
                      {bid.category.length === 0 && '—'}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ py: 1.8 }}>
                    <Chip
                      label={bid.status}
                      size="small"
                      sx={{
                        height: 24,
                        fontSize: '0.75rem',
                        bgcolor: alpha(getStatusColor(bid.status), 0.1),
                        color: getStatusColor(bid.status),
                        fontWeight: 'medium',
                        borderRadius: 1
                      }}
                    />
                  </TableCell>

                  {userRole === 'Admin' && (
                    <TableCell align="center">
                      <Box display="flex" justifyContent="center" gap={1}>
                        <Tooltip title="Edit Bid">
                          <IconButton
                            onClick={() => handleEdit(bid)}
                            size="small"
                            sx={{
                              color: '#4285f4',
                              backgroundColor: alpha('#4285f4', 0.05),
                              '&:hover': {
                                backgroundColor: alpha('#4285f4', 0.15)
                              },
                              transition: 'all 0.2s',
                              padding: 1,
                              border: `1px solid ${alpha('#4285f4', 0.2)}`
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Bid">
                          <IconButton
                            size="small"
                            onClick={() =>
                              openDeleteDialog(bid.bidId, bid.title)
                            }
                            sx={{
                              color: theme.palette.error.main,
                              backgroundColor: alpha(
                                theme.palette.error.main,
                                0.05
                              ),
                              '&:hover': {
                                backgroundColor: alpha(
                                  theme.palette.error.main,
                                  0.15
                                )
                              },
                              transition: 'all 0.2s',
                              padding: 1,
                              border: `1px solid ${alpha(
                                theme.palette.error.main,
                                0.2
                              )}`
                            }}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 2
                    }}
                  >
                    <FilterAltIcon
                      sx={{
                        fontSize: 40,
                        color: alpha(theme.palette.text.secondary, 0.6)
                      }}
                    />
                    <Typography
                      variant="subtitle1"
                      color="text.secondary"
                      sx={{ fontWeight: 500 }}
                    >
                      No bids found. Try adjusting your search filters.
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={totalRecords}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        sx={{
          borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          bgcolor: alpha(theme.palette.background.paper, 0.5),
          '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows':
            {
              my: 1,
              fontWeight: 500
            },
          '& .MuiTablePagination-select': {
            borderRadius: 1.5,
            '&:focus': {
              backgroundColor: alpha(theme.palette.primary.main, 0.05)
            }
          },
          '& .MuiTablePagination-actions': {
            '& .MuiIconButton-root': {
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.1)
              }
            }
          }
        }}
      />
    </Card>
  );

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        bgcolor: alpha(theme.palette.background.default, 0.6),
        borderRadius: 2,
        minHeight: '100vh'
      }}
    >
      <Typography
        variant="h4"
        gutterBottom
        sx={{
          fontWeight: 'bold',
          color: theme.palette.primary.main,
          mb: 4,
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          '&:after': {
            content: '""',
            position: 'absolute',
            bottom: -8,
            left: 0,
            width: 90,
            height: 4,
            borderRadius: 2,
            backgroundColor: theme.palette.primary.main
          }
        }}
      >
        {showSearch ? 'Modify Bids' : 'Edit Bid Details'}
      </Typography>

      {showSearch ? (
        <>
          <Card
            sx={{
              mb: 4,
              borderRadius: 2,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
              overflow: 'hidden',
              border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`
            }}
          >
            <Box
              sx={{
                p: 2.5,
                background: `linear-gradient(to right, ${alpha(
                  theme.palette.primary.main,
                  0.05
                )}, ${alpha(theme.palette.primary.main, 0.02)})`,
                borderBottom: `1px solid ${alpha(
                  theme.palette.primary.main,
                  0.1
                )}`
              }}
            >
              <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
                <SearchIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Search Filters
              </Typography>
            </Box>
            <CardContent sx={{ p: 3 }}>
              <Grid container spacing={2.5}>
                {/* First row - 3 fields */}
                <Grid item xs={12} sm={4} md={3}>
                  <TextField
                    size="small"
                    fullWidth
                    label="Title"
                    variant="outlined"
                    value={searchFormData.title}
                    onChange={(e) =>
                      setSearchFormData({
                        ...searchFormData,
                        title: e.target.value
                      })
                    }
                    InputProps={{
                      sx: {
                        borderRadius: 1.5,
                        height: 40,
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: alpha(theme.palette.primary.main, 0.5)
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderWidth: 2
                        }
                      }
                    }}
                    sx={{
                      '& .MuiInputLabel-root': {
                        color: theme.palette.text.secondary,
                        fontSize: '0.875rem',
                        transform: 'translate(14px, 11px) scale(1)'
                      },
                      '& .MuiInputLabel-shrink': {
                        transform: 'translate(14px, -6px) scale(0.75)'
                      },
                      '& .MuiInputLabel-root.Mui-focused': {
                        fontWeight: 500
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={4} md={3}>
                  <FormControl fullWidth variant="outlined" size="small">
                    <InputLabel
                      sx={{
                        color: theme.palette.text.secondary,
                        fontSize: '0.875rem',
                        transform: 'translate(14px, 11px) scale(1)',
                        '&.MuiInputLabel-shrink': {
                          transform: 'translate(14px, -6px) scale(0.75)'
                        },
                        '&.Mui-focused': {
                          fontWeight: 500
                        },
                        backgroundColor: 'white',
                        paddingLeft: '4px',
                        paddingRight: '4px',
                        zIndex: 1
                      }}
                    >
                      Category
                    </InputLabel>
                    <Select
                      open={openCategorySelect}
                      onOpen={() => setOpenCategorySelect(true)}
                      onClose={() => setOpenCategorySelect(false)}
                      multiple
                      value={searchFormData.categories}
                      onChange={(e) => {
                        const selectedValues = e.target.value as any[];

                        // Handle "Select All" logic
                        if (selectedValues.includes('SELECT_ALL')) {
                          const allCategoryIDs = categories?.map(
                            (cat) => cat.CategoryID
                          );
                          const isAllSelected =
                            searchFormData.categories?.length ===
                            categories?.length;

                          // If all categories are selected, deselect all; otherwise, select all
                          const finalCategories = isAllSelected
                            ? []
                            : allCategoryIDs;

                          setSearchFormData({
                            ...searchFormData,
                            categories: finalCategories
                          });
                        } else {
                          // Ensure no invalid "Unknown" category is included
                          const validSelectedValues = selectedValues.filter(
                            (value) =>
                              categories.some((cat) => cat.CategoryID === value)
                          );

                          setSearchFormData({
                            ...searchFormData,
                            categories: validSelectedValues
                          });
                        }
                      }}
                      sx={{
                        borderRadius: 1.5,
                        height: 40,
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: alpha(theme.palette.primary.main, 0.5)
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderWidth: 2
                        },
                        '& .MuiSelect-select': {
                          paddingRight: '32px !important'
                        }
                      }}
                      renderValue={(selected) => {
                        const selectedArray = selected as any[];
                        const totalSelected = selectedArray.length;

                        if (totalSelected === 0) {
                          return (
                            <Box sx={{ color: 'text.secondary' }}>
                              Select categories
                            </Box>
                          );
                        }

                        return (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              width: '100%',
                              overflow: 'hidden',
                              position: 'relative'
                            }}
                          >
                            {/* Scrollable chips container */}
                            <Box
                              sx={{
                                display: 'flex',
                                gap: 0.5,
                                overflow: 'hidden',
                                flexGrow: 1,
                                maskImage:
                                  totalSelected > 1
                                    ? 'linear-gradient(to right, black calc(100% - 60px), transparent 100%)'
                                    : 'none',
                                WebkitMaskImage:
                                  totalSelected > 1
                                    ? 'linear-gradient(to right, black calc(100% - 60px), transparent 100%)'
                                    : 'none'
                              }}
                            >
                              {selectedArray.map((CategoryID) => {
                                const categoryName =
                                  categories.find(
                                    (c) => c.CategoryID === CategoryID
                                  )?.CategoryName || 'Unknown';
                                return (
                                  <Chip
                                    key={CategoryID}
                                    label={categoryName}
                                    size="small"
                                    sx={{
                                      height: 24,
                                      fontSize: '0.75rem',
                                      bgcolor: alpha(
                                        theme.palette.primary.main,
                                        0.1
                                      ),
                                      color: theme.palette.primary.main,
                                      fontWeight: 500,
                                      borderRadius: 1,
                                      flexShrink: 0,
                                      maxWidth: '120px',
                                      '& .MuiChip-label': {
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        paddingLeft: '8px',
                                        paddingRight: '8px'
                                      }
                                    }}
                                  />
                                );
                              })}
                            </Box>

                            {/* Count badge - always visible on the right */}
                            {totalSelected > 3 && (
                              <Chip
                                label={`${totalSelected}`}
                                size="small"
                                sx={{
                                  height: 24,
                                  fontSize: '0.75rem',
                                  bgcolor: alpha(
                                    theme.palette.primary.main,
                                    0.2
                                  ),
                                  color: theme.palette.primary.main,
                                  fontWeight: 600,
                                  borderRadius: 1,
                                  minWidth: '40px',
                                  flexShrink: 0,
                                  position: 'absolute',
                                  right: 0,
                                  backgroundColor: 'white',
                                  boxShadow:
                                    '-4px 0 8px rgba(255, 255, 255, 0.9)'
                                }}
                              />
                            )}
                          </Box>
                        );
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            maxHeight: 350,
                            borderRadius: 1.5,
                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                            marginTop: 1
                          }
                        },
                        anchorOrigin: {
                          vertical: 'bottom',
                          horizontal: 'left'
                        },
                        transformOrigin: {
                          vertical: 'top',
                          horizontal: 'left'
                        }
                      }}
                    >
                      {/* Select All Option */}
                      <MenuItem value="SELECT_ALL">
                        <Checkbox
                          checked={
                            searchFormData.categories.length ===
                            categories.length
                          }
                          indeterminate={
                            searchFormData.categories.length > 0 &&
                            searchFormData.categories.length < categories.length
                          }
                        />
                        <ListItemText
                          primary="Select All"
                          sx={{ fontWeight: 'bold', color: 'primary.main' }}
                        />
                      </MenuItem>

                      {/* Individual Category Options */}
                      {categories?.map((category) => (
                        <MenuItem
                          key={category.CategoryID}
                          value={category.CategoryID}
                          sx={{
                            fontSize: '0.875rem',
                            '&.Mui-selected': {
                              backgroundColor: alpha(
                                theme.palette.primary.main,
                                0.08
                              ),
                              '&:hover': {
                                backgroundColor: alpha(
                                  theme.palette.primary.main,
                                  0.12
                                )
                              }
                            }
                          }}
                        >
                          <Checkbox
                            checked={
                              searchFormData.categories.indexOf(
                                category.CategoryID
                              ) > -1
                            }
                          />
                          {category.CategoryName}
                        </MenuItem>
                      ))}

                      <MenuItem
                        disableRipple
                        sx={{
                          display: 'flex',
                          justifyContent: 'flex-end',
                          borderTop: '1px solid #ddd',
                          mt: 1,
                          pt: 1
                        }}
                      >
                        <button
                          style={{
                            backgroundColor: '#1976d2',
                            color: 'white',
                            padding: '5px 12px',
                            borderRadius: '6px',
                            border: 'none',
                            cursor: 'pointer',
                            fontWeight: '600'
                          }}
                          onClick={() => setOpenCategorySelect(false)}
                        >
                          Apply
                        </button>
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4} md={3}>
                  <FormControl fullWidth variant="outlined" size="small">
                    <InputLabel
                      sx={{
                        color: theme.palette.text.secondary,
                        fontSize: '0.875rem',
                        transform: 'translate(14px, 11px) scale(1)',
                        '&.MuiInputLabel-shrink': {
                          transform: 'translate(14px, -6px) scale(0.75)'
                        },
                        '&.Mui-focused': {
                          fontWeight: 500
                        },
                        backgroundColor: 'white',
                        paddingLeft: '4px',
                        paddingRight: '4px',
                        zIndex: 1
                      }}
                    >
                      Status
                    </InputLabel>
                    <Select
                      open={openStatusSelect}
                      onOpen={() => setOpenStatusSelect(true)}
                      onClose={() => setOpenStatusSelect(false)}
                      multiple
                      value={searchFormData.status}
                      onChange={(e) => {
                        const selectedValues = e.target.value as any[];

                        // Handle "Select All" logic
                        if (selectedValues.includes('SELECT_ALL')) {
                          const allStatusIDs = statuses?.map(
                            (status) => status.StatusID
                          );
                          const isAllSelected =
                            searchFormData.status?.length === statuses?.length;

                          // If all statuses are selected, deselect all; otherwise, select all
                          const finalStatuses = isAllSelected
                            ? []
                            : allStatusIDs;

                          setSearchFormData({
                            ...searchFormData,
                            status: finalStatuses
                          });
                        } else {
                          // Ensure no invalid "Unknown" status is included
                          const validSelectedValues = selectedValues.filter(
                            (value) =>
                              statuses.some(
                                (status) => status.StatusID === value
                              )
                          );

                          setSearchFormData({
                            ...searchFormData,
                            status: validSelectedValues
                          });
                        }
                      }}
                      sx={{
                        borderRadius: 1.5,
                        height: 40,
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: alpha(theme.palette.primary.main, 0.5)
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderWidth: 2
                        },
                        '& .MuiSelect-select': {
                          paddingRight: '32px !important'
                        }
                      }}
                      renderValue={(selected) => {
                        const selectedArray = selected as number[];
                        const totalSelected = selectedArray.length;

                        if (totalSelected === 0) {
                          return (
                            <Box sx={{ color: 'text.secondary' }}>
                              Select statuses
                            </Box>
                          );
                        }

                        return (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              width: '100%',
                              overflow: 'hidden',
                              position: 'relative'
                            }}
                          >
                            {/* Scrollable chips container */}
                            <Box
                              sx={{
                                display: 'flex',
                                gap: 0.5,
                                overflow: 'hidden',
                                flexGrow: 1,
                                maskImage:
                                  totalSelected > 1
                                    ? 'linear-gradient(to right, black calc(100% - 60px), transparent 100%)'
                                    : 'none',
                                WebkitMaskImage:
                                  totalSelected > 1
                                    ? 'linear-gradient(to right, black calc(100% - 60px), transparent 100%)'
                                    : 'none'
                              }}
                            >
                              {selectedArray.map((statusId) => {
                                const statusName =
                                  statuses.find((s) => s.StatusID === statusId)
                                    ?.Status || 'Unknown';
                                return (
                                  <Chip
                                    key={statusId}
                                    label={statusName}
                                    size="small"
                                    sx={{
                                      height: 24,
                                      fontSize: '0.75rem',
                                      bgcolor: alpha(
                                        theme.palette.primary.main,
                                        0.1
                                      ),
                                      color: theme.palette.primary.main,
                                      fontWeight: 500,
                                      borderRadius: 1,
                                      flexShrink: 0,
                                      maxWidth: '120px',
                                      '& .MuiChip-label': {
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        paddingLeft: '8px',
                                        paddingRight: '8px'
                                      }
                                    }}
                                  />
                                );
                              })}
                            </Box>

                            {/* Count badge - always visible on the right */}
                            {totalSelected > 1 && (
                              <Chip
                                label={`${totalSelected}`}
                                size="small"
                                sx={{
                                  height: 24,
                                  fontSize: '0.75rem',
                                  bgcolor: alpha(
                                    theme.palette.primary.main,
                                    0.2
                                  ),
                                  color: theme.palette.primary.main,
                                  fontWeight: 600,
                                  borderRadius: 1,
                                  minWidth: '40px',
                                  flexShrink: 0,
                                  position: 'absolute',
                                  right: 0,
                                  backgroundColor: 'white',
                                  boxShadow:
                                    '-4px 0 8px rgba(255, 255, 255, 0.9)'
                                }}
                              />
                            )}
                          </Box>
                        );
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            maxHeight: 350,
                            borderRadius: 1.5,
                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                            marginTop: 1
                          }
                        },
                        anchorOrigin: {
                          vertical: 'bottom',
                          horizontal: 'left'
                        },
                        transformOrigin: {
                          vertical: 'top',
                          horizontal: 'left'
                        }
                      }}
                    >
                      {/* Select All Option */}
                      <MenuItem value="SELECT_ALL">
                        <Checkbox
                          checked={
                            searchFormData.status.length === statuses.length
                          }
                          indeterminate={
                            searchFormData.status.length > 0 &&
                            searchFormData.status.length < statuses.length
                          }
                        />
                        <ListItemText
                          primary="Select All"
                          sx={{ fontWeight: 'bold', color: 'primary.main' }}
                        />
                      </MenuItem>

                      {/* Individual Status Options */}
                      {statuses?.map((status) => (
                        <MenuItem
                          key={status.StatusID}
                          value={status.StatusID}
                          sx={{
                            fontSize: '0.875rem',
                            '&.Mui-selected': {
                              backgroundColor: alpha(
                                theme.palette.secondary.main,
                                0.08
                              ),
                              '&:hover': {
                                backgroundColor: alpha(
                                  theme.palette.secondary.main,
                                  0.12
                                )
                              }
                            }
                          }}
                        >
                          <Checkbox
                            checked={
                              searchFormData.status.indexOf(status.StatusID) >
                              -1
                            }
                          />
                          {status.Status}
                        </MenuItem>
                      ))}

                      <MenuItem
                        disableRipple
                        sx={{
                          display: 'flex',
                          justifyContent: 'flex-end',
                          borderTop: '1px solid #ddd',
                          mt: 1,
                          pt: 1
                        }}
                      >
                        <button
                          style={{
                            backgroundColor: '#1976d2',
                            color: 'white',
                            padding: '5px 12px',
                            borderRadius: '6px',
                            border: 'none',
                            cursor: 'pointer',
                            fontWeight: '600'
                          }}
                          onClick={() => setOpenStatusSelect(false)}
                        >
                          Apply
                        </button>
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4} md={3}>
                  <FormControl fullWidth variant="outlined" size="small">
                    <InputLabel
                      sx={{
                        color: theme.palette.text.secondary,
                        fontSize: '0.875rem',
                        transform: 'translate(14px, 11px) scale(1)',
                        '&.MuiInputLabel-shrink': {
                          transform: 'translate(14px, -6px) scale(0.75)'
                        },
                        '&.Mui-focused': {
                          fontWeight: 500
                        },
                        backgroundColor: 'white',
                        paddingLeft: '4px',
                        paddingRight: '4px',
                        zIndex: 1
                      }}
                    >
                      Type
                    </InputLabel>
                    <Select
                      open={openTypeSelect}
                      onOpen={() => setOpenTypeSelect(true)}
                      onClose={() => setOpenTypeSelect(false)}
                      multiple
                      value={searchFormData.type}
                      onChange={(e) => {
                        const selectedValues = e.target.value as any[];
                        // Handle "Select All" logic
                        if (selectedValues.includes('SELECT_ALL')) {
                          const allTypeIDs = bidTypes.map(
                            (type) => type.TypeID
                          );
                          const isAllSelected =
                            searchFormData.type.length === bidTypes.length;

                          // If all types are selected, deselect all; otherwise, select all
                          const finalTypes = isAllSelected ? [] : allTypeIDs;

                          setSearchFormData({
                            ...searchFormData,
                            type: finalTypes
                          });
                        } else {
                          // Ensure no invalid "Unknown" type is included
                          const validSelectedValues = selectedValues.filter(
                            (value) =>
                              bidTypes.some((type) => type.TypeID === value)
                          );

                          setSearchFormData({
                            ...searchFormData,
                            type: validSelectedValues
                          });
                        }
                      }}
                      sx={{
                        borderRadius: 1.5,
                        height: 40,
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: alpha(theme.palette.primary.main, 0.5)
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderWidth: 2
                        },
                        '& .MuiSelect-select': {
                          paddingRight: '32px !important'
                        }
                      }}
                      renderValue={(selected) => {
                        const selectedArray = selected as number[];
                        const totalSelected = selectedArray.length;

                        if (totalSelected === 0) {
                          return (
                            <Box sx={{ color: 'text.secondary' }}>
                              Select types
                            </Box>
                          );
                        }

                        return (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              width: '100%',
                              overflow: 'hidden',
                              position: 'relative'
                            }}
                          >
                            {/* Scrollable chips container */}
                            <Box
                              sx={{
                                display: 'flex',
                                gap: 0.5,
                                overflow: 'hidden',
                                flexGrow: 1,
                                maskImage:
                                  totalSelected > 1
                                    ? 'linear-gradient(to right, black calc(100% - 60px), transparent 100%)'
                                    : 'none',
                                WebkitMaskImage:
                                  totalSelected > 1
                                    ? 'linear-gradient(to right, black calc(100% - 60px), transparent 100%)'
                                    : 'none'
                              }}
                            >
                              {selectedArray.map((TypeID) => {
                                const TypeName =
                                  bidTypes.find((t) => t.TypeID === TypeID)
                                    ?.Type || 'Unknown';
                                return (
                                  <Chip
                                    key={TypeID}
                                    label={TypeName}
                                    size="small"
                                    sx={{
                                      height: 24,
                                      fontSize: '0.75rem',
                                      bgcolor: alpha(
                                        theme.palette.primary.main,
                                        0.1
                                      ),
                                      color: theme.palette.primary.main,
                                      fontWeight: 500,
                                      borderRadius: 1,
                                      flexShrink: 0,
                                      maxWidth: '120px',
                                      '& .MuiChip-label': {
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        paddingLeft: '8px',
                                        paddingRight: '8px'
                                      }
                                    }}
                                  />
                                );
                              })}
                            </Box>

                            {/* Count badge - always visible on the right */}
                            {totalSelected > 1 && (
                              <Chip
                                label={`${totalSelected}`}
                                size="small"
                                sx={{
                                  height: 24,
                                  fontSize: '0.75rem',
                                  bgcolor: alpha(
                                    theme.palette.primary.main,
                                    0.2
                                  ),
                                  color: theme.palette.primary.main,
                                  fontWeight: 600,
                                  borderRadius: 1,
                                  minWidth: '40px',
                                  flexShrink: 0,
                                  position: 'absolute',
                                  right: 0,
                                  backgroundColor: 'white',
                                  boxShadow:
                                    '-4px 0 8px rgba(255, 255, 255, 0.9)'
                                }}
                              />
                            )}
                          </Box>
                        );
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            maxHeight: 350,
                            borderRadius: 1.5,
                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                            marginTop: 1
                          }
                        },
                        anchorOrigin: {
                          vertical: 'bottom',
                          horizontal: 'left'
                        },
                        transformOrigin: {
                          vertical: 'top',
                          horizontal: 'left'
                        }
                      }}
                    >
                      {/* Select All Option */}
                      <MenuItem value="SELECT_ALL">
                        <Checkbox
                          checked={
                            searchFormData.type.length === bidTypes.length
                          }
                          indeterminate={
                            searchFormData.type.length > 0 &&
                            searchFormData.type.length < bidTypes.length
                          }
                        />
                        <ListItemText
                          primary="Select All"
                          sx={{ fontWeight: 'bold', color: 'primary.main' }}
                        />
                      </MenuItem>

                      {/* Individual Type Options */}
                      {bidTypes.map((type) => (
                        <MenuItem
                          key={type.TypeID}
                          value={type.TypeID}
                          sx={{
                            fontSize: '0.875rem',
                            '&.Mui-selected': {
                              backgroundColor: alpha(
                                theme.palette.info.main,
                                0.08
                              ),
                              '&:hover': {
                                backgroundColor: alpha(
                                  theme.palette.info.main,
                                  0.12
                                )
                              }
                            }
                          }}
                        >
                          <Checkbox
                            checked={
                              searchFormData.type.indexOf(type.TypeID) > -1
                            }
                          />
                          {type.Type}
                        </MenuItem>
                      ))}
                      <MenuItem
                        disableRipple
                        sx={{
                          display: 'flex',
                          justifyContent: 'flex-end',
                          borderTop: '1px solid #ddd',
                          mt: 1,
                          pt: 1
                        }}
                      >
                        <button
                          style={{
                            backgroundColor: '#1976d2',
                            color: 'white',
                            padding: '5px 12px',
                            borderRadius: '6px',
                            border: 'none',
                            cursor: 'pointer',
                            fontWeight: '600'
                          }}
                          onClick={() => setOpenTypeSelect(false)}
                        >
                          Apply
                        </button>
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Second row with date picker and buttons */}
                {/* <Grid item xs={12} sm={4} md={3}>
                  <DatePicker
                    label="Bid Deadline"
                    value={searchFormData.bidDeadline}
                    onChange={(date) =>
                      setSearchFormData({
                        ...searchFormData,
                        bidDeadline: date
                      })
                    }
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: 'small',
                        variant: 'outlined',
                        InputProps: {
                          startAdornment: (
                            <CalendarToday
                              sx={{
                                color: theme.palette.text.secondary,
                                mr: 1,
                                fontSize: '1rem'
                              }}
                            />
                          ),
                          sx: {
                            borderRadius: 1.5,
                            height: 40,
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: alpha(
                                theme.palette.primary.main,
                                0.5
                              )
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderWidth: 2
                            }
                          }
                        },
                        sx: {
                          '& .MuiInputLabel-root': {
                            color: theme.palette.text.secondary,
                            fontSize: '0.875rem',
                            transform: 'translate(14px, 11px) scale(1)'
                          },
                          '& .MuiInputLabel-shrink': {
                            transform: 'translate(14px, -6px) scale(0.75)'
                          },
                          '& .MuiInputLabel-root.Mui-focused': {
                            fontWeight: 500
                          }
                        }
                      },
                      desktopPaper: {
                        sx: {
                          borderRadius: 2,
                          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                          '& .MuiPickersDay-root.Mui-selected': {
                            backgroundColor: theme.palette.primary.main,
                            '&:hover': {
                              backgroundColor: theme.palette.primary.dark
                            }
                          }
                        }
                      }
                    }}
                  />
                </Grid> */}

                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 2,
                      justifyContent: 'flex-end',
                      height: '100%',
                      alignItems: 'center'
                    }}
                  >
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleSearch}
                      startIcon={<SearchIcon />}
                      size="small"
                      sx={{
                        borderRadius: 1.5,
                        px: 3,
                        py: 0.8,
                        fontWeight: 'bold',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          boxShadow: '0 4px 10px rgba(0, 0, 0, 0.18)',
                          transform: 'translateY(-2px)'
                        }
                      }}
                    >
                      Search
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={handleClear}
                      startIcon={<ClearAll />}
                      size="small"
                      sx={{
                        borderRadius: 1.5,
                        px: 3,
                        py: 0.8,
                        fontWeight: 'bold',
                        borderWidth: '1px',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          borderWidth: '1px',
                          backgroundColor: alpha(
                            theme.palette.primary.main,
                            0.04
                          )
                        }
                      }}
                    >
                      Clear
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
          <ResultsTable />
        </>
      ) : (
        <Card
          elevation={3}
          sx={{
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
            border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`
          }}
        >
          <CardContent>
            <EditBid
              bid={selectedBid}
              categories={categories}
              statuses={statuses}
              bidTypes={bidTypes}
              categoryMap={categoryMap}
              statusMap={statusMap}
              typeMap={typeMap}
              onCancel={handleEditCancel}
              onSuccess={handleEditSuccess}
            />
          </CardContent>
        </Card>
      )}

      {loading && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: alpha(theme.palette.background.default, 0.7),
            backdropFilter: 'blur(4px)',
            zIndex: 9999
          }}
        >
          <Paper
            elevation={4}
            sx={{
              p: 3,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              borderRadius: 2,
              boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)'
            }}
          >
            <CircularProgress size={48} sx={{ mb: 2 }} />
            <Typography variant="subtitle1">Loading data...</Typography>
          </Paper>
        </Box>
      )}

      {/* Confirmation delete dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={closeDeleteDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)',
            minWidth: { xs: '85%', sm: 400 },
            border: `1px solid ${alpha(theme.palette.error.main, 0.1)}`,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle
          id="alert-dialog-title"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            pb: 1,
            pt: 2.5,
            color: theme.palette.error.main,
            fontWeight: 'bold',
            borderBottom: `1px solid ${alpha(theme.palette.error.main, 0.1)}`,
            backgroundColor: alpha(theme.palette.error.main, 0.03)
          }}
        >
          <Warning color="error" />
          Confirm Deactivation
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <DialogContentText
            id="alert-dialog-description"
            sx={{ color: theme.palette.text.primary }}
          >
            Are you sure you want to deactivate the bid:{' '}
            <Typography
              component="span"
              fontWeight="bold"
              color={theme.palette.text.primary}
            >
              {deleteDialog.bidTitle}
            </Typography>
            ? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
          <Button
            onClick={closeDeleteDialog}
            variant="outlined"
            sx={{
              borderRadius: 1.5,
              px: 3,
              py: 1,
              fontWeight: 'medium',
              borderWidth: '1.5px',
              '&:hover': {
                borderWidth: '1.5px',
                backgroundColor: alpha(theme.palette.primary.main, 0.04)
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            autoFocus
            startIcon={<Delete />}
            sx={{
              borderRadius: 1.5,
              px: 3,
              py: 1,
              fontWeight: 'bold',
              boxShadow: '0 4px 10px rgba(211, 47, 47, 0.25)',
              '&:hover': {
                boxShadow: '0 6px 12px rgba(211, 47, 47, 0.35)'
              }
            }}
          >
            Deactivate
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BidManagement;
